import { ChevronRight, User, Settings, LogOut, Sun, Moon, Monitor, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { useAuthStore } from '@/core/stores/auth.store';
import { useLogout } from '@/features/auth/public';
import { useUpdatePreferences } from '@/features/settings/public';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { useUIStore } from '@/core/stores/ui.store';
import { useOrganizationStore } from '@/core/stores/organization.store';
import i18n from '@/core/i18n/config';
import type { Theme, SettingsSectionId } from '@/core/stores/ui.store';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
] as const;

const THEMES = [
  { value: 'light', icon: Sun, labelKey: 'sidebar.user.theme.light' },
  { value: 'dark', icon: Moon, labelKey: 'sidebar.user.theme.dark' },
  { value: 'system', icon: Monitor, labelKey: 'sidebar.user.theme.system' },
] as const;

export function SidebarUserMenu() {
  const { t } = useTranslation('layout');
  const user = useAuthStore((s) => s.user);
  const { logout } = useLogout();
  const navigate = useNavigate();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const setActiveSettingsSectionId = useUIStore((s) => s.setActiveSettingsSectionId);
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);
  const updatePreferences = useUpdatePreferences();

  const navigateToSettings = (section: SettingsSectionId) => {
    setActiveSettingsSectionId(section);
    navigate(`/${activeOrganizationId}/settings`);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const currentTheme = THEMES.find((th) => th.value === theme) || THEMES[2];

  const handleLanguageChange = (langCode: (typeof LANGUAGES)[number]['code']) => {
    i18n.changeLanguage(langCode);
    updatePreferences.mutate({ language: langCode });
  };

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  if (!user) return null;

  return (
    <div className="px-3 py-3 border-t border-border shrink-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-3 w-full px-3 py-3 rounded-xl bg-muted/40 hover:bg-primary/5 transition-colors group text-left"
          >
            <UserAvatar
              name={user.name}
              color={user.color || 'indigo-deep'}
              src={user.avatarUrl}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate leading-none mb-0.5">
                {user.name}
              </p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="top" align="start" className="w-56">
          <DropdownMenuItem onClick={() => navigateToSettings('profile')}>
            <User className="w-4 h-4 mr-2" />
            {t('sidebar.user.profile')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigateToSettings('org-info')}>
            <Settings className="w-4 h-4 mr-2" />
            {t('sidebar.user.settings')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />

          <DropdownMenuSub>
            <DropdownMenuSubTrigger inset data-testid="sidebar-user-language-trigger">
              <Globe className="w-4 h-4 mr-2" />
              <span>{currentLang.label}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {LANGUAGES.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  data-testid={`sidebar-user-language-option-${lang.code}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger inset>
              <currentTheme.icon className="w-4 h-4 mr-2" />
              <span>{t(currentTheme.labelKey)}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {THEMES.map((th) => (
                <DropdownMenuItem key={th.value} onClick={() => handleThemeChange(th.value)}>
                  <th.icon className="w-4 h-4 mr-2" />
                  {t(th.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
            <LogOut className="w-4 h-4 mr-2" />
            {t('sidebar.user.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
